<template>
    <div class="shop-dmf-page">
        <h1 class="mod-title">
            <span class="text">支付宝扫码支付</span>
        </h1>
        <div class="mod-ct" v-loading="loading">
            <div class="amount" v-if="orderInfo.amount">￥{{ orderInfo.amount }}</div>
            <div class="qr-image" v-if="qrCodeUrl">
                <img :src="qrCodeUrl" alt="支付宝二维码" />
            </div>
            <div class="qr-tip" v-if="qrCodeUrl">
                <p>请使用支付宝扫一扫</p>
                <p>扫描二维码完成支付</p>
            </div>
            <div class="order-info" v-if="orderInfo.goods_name">
                <div class="info-row">
                    <span class="label">商品名称</span>
                    <span>{{ orderInfo.goods_name }}</span>
                </div>
                <div class="info-row">
                    <span class="label">订单编号</span>
                    <span>{{ orderInfo.order_id }}</span>
                </div>
                <div class="info-row">
                    <span class="label">下单时间</span>
                    <span>{{ orderInfo.created_at }}</span>
                </div>
            </div>
            <div class="pay-status" v-if="payStatus === 'success'">
                <div class="status-icon success">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 13L9 17L19 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                            stroke-linejoin="round" />
                    </svg>
                </div>
                <p class="status-text">支付成功</p>
                <p class="status-hint">即将跳转...</p>
            </div>
            <div class="pay-actions" v-if="qrCodeUrl && payStatus !== 'success'">
                <el-button type="primary" @click="checkPayResult" :loading="checking">我已支付</el-button>
            </div>
            <div class="error-tip" v-if="errorMsg">
                <p>{{ errorMsg }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import { ElMessage } from 'element-plus'
import { useRoute, useRouter } from 'vue-router';
import QRCode from 'qrcode';

const baseURL = process.env.VUE_APP_API_BASE_URL;

export default defineComponent({
    name: 'ShopAlipayDMF',
    setup() {
        const route = useRoute();
        const router = useRouter();
        return { route, router };
    },
    data() {
        return {
            tenantKey: '',
            orderId: '',
            queryPassword: '',
            orderInfo: {},
            qrCodeUrl: '',
            loading: true,
            checking: false,
            payStatus: '',
            errorMsg: '',
            pollTimer: null,
        }
    },
    mounted() {
        this.tenantKey = this.route.query.tenant_key || '';
        this.orderId = this.route.query.order_id || '';
        this.queryPassword = this.route.query.query_password || '';
        if (!this.tenantKey || !this.orderId) {
            this.errorMsg = '缺少必要参数，无法加载支付页面';
            this.loading = false;
            return;
        }
        this.loadPayInfo();
        // 轮询支付状态
        this.pollTimer = setInterval(() => {
            this.pollPayResult();
        }, 3000);
    },
    beforeUnmount() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
        }
    },
    methods: {
        async loadPayInfo() {
            this.loading = true;
            try {
                const res = await axios.post(`${baseURL}/api/shop/order/detail`, {
                    tenant_key: this.tenantKey,
                    order_id: this.orderId,
                    query_password: this.queryPassword,
                });
                if (res.data.code === 0 || res.data.code === 200) {
                    this.orderInfo = res.data.data;
                    // 如果已完成支付
                    if (this.orderInfo.status === 1) {
                        this.payStatus = 'success';
                        this.onPaySuccess();
                        return;
                    }
                    // 当面付：pay_url 是支付宝二维码URL，需要前端生成二维码图片
                    if (this.orderInfo.pay_type === 'alipay_dmf' && this.orderInfo.pay_url) {
                        QRCode.toDataURL(this.orderInfo.pay_url, { width: 280, margin: 1 })
                            .then(url => { this.qrCodeUrl = url; })
                            .catch(() => { this.errorMsg = '生成二维码失败'; });
                    } else if (this.orderInfo.qr_code_url) {
                        // 兼容后端直接返回base64二维码图片
                        this.qrCodeUrl = this.orderInfo.qr_code_url;
                    } else {
                        this.errorMsg = '暂无支付二维码，请联系商家';
                    }
                } else {
                    this.errorMsg = res.data.msg || '获取支付信息失败';
                }
            } catch (e) {
                console.error('获取支付信息失败:', e);
                this.errorMsg = '获取支付信息失败';
            } finally {
                this.loading = false;
            }
        },
        async pollPayResult() {
            if (this.payStatus === 'success') return;
            try {
                const res = await axios.post(`${baseURL}/api/shop/order/detail`, {
                    tenant_key: this.tenantKey,
                    order_id: this.orderId,
                    query_password: this.queryPassword,
                });
                if (res.data.code === 0 || res.data.code === 200) {
                    if (res.data.data.status === 1) {
                        this.orderInfo = res.data.data;
                        this.payStatus = 'success';
                        this.onPaySuccess();
                    }
                }
            } catch (e) {
                // 静默失败
            }
        },
        async checkPayResult() {
            this.checking = true;
            try {
                const res = await axios.post(`${baseURL}/api/shop/order/detail`, {
                    tenant_key: this.tenantKey,
                    order_id: this.orderId,
                    query_password: this.queryPassword,
                });
                if (res.data.code === 0 || res.data.code === 200) {
                    if (res.data.data.status === 1) {
                        this.orderInfo = res.data.data;
                        this.payStatus = 'success';
                        this.onPaySuccess();
                    } else {
                        ElMessage.warning('订单尚未支付，请扫码完成支付');
                    }
                }
            } catch (e) {
                ElMessage.error('查询失败');
            } finally {
                this.checking = false;
            }
        },
        onPaySuccess() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
            ElMessage.success('支付成功');
            setTimeout(() => {
                this.router.push({
                    path: '/shopOrders',
                    query: { tenant_key: this.tenantKey }
                });
            }, 2000);
        },
    }
})
</script>

<style scoped lang="scss">
.shop-dmf-page {
    min-height: 100vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.mod-title {
    height: 60px;
    line-height: 60px;
    text-align: center;
    width: 100%;
    border-bottom: 1px solid #e5e5e5;
    background: #fff;

    .text {
        font-size: 20px;
        color: #333;
        font-weight: normal;
    }
}

.mod-ct {
    max-width: 610px;
    width: 100%;
    margin: 30px auto;
    text-align: center;
    color: #333;
    padding: 0 1rem;

    .amount {
        font-size: 42px;
        margin-top: 20px;
        font-weight: 600;
        color: #333;
    }

    .qr-image {
        margin-top: 30px;
        display: flex;
        justify-content: center;

        img {
            width: 280px;
            height: 280px;
            border: 1px solid #e5e5e5;
            border-radius: 0.5rem;
        }
    }

    .qr-tip {
        margin-top: 20px;

        p {
            margin: 0;
            font-size: 15px;
            line-height: 1.8;
            color: #64748b;
        }
    }

    .order-info {
        margin-top: 30px;
        padding: 1rem 1.5rem;
        background: #f8fafc;
        border-radius: 0.6rem;
        text-align: left;

        .info-row {
            display: flex;
            justify-content: space-between;
            padding: 0.3rem 0;
            font-size: 0.85rem;

            .label {
                color: #94a3b8;
                flex-shrink: 0;
            }

            span:last-child {
                color: #475569;
                text-align: right;
            }
        }
    }

    .pay-status {
        margin-top: 40px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;

        .status-icon {
            width: 64px;
            height: 64px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;

            &.success {
                background: #dcfce7;
                color: #16a34a;
            }

            svg {
                width: 36px;
                height: 36px;
            }
        }

        .status-text {
            font-size: 1.2rem;
            font-weight: 600;
            color: #16a34a;
            margin: 0;
        }

        .status-hint {
            font-size: 0.85rem;
            color: #94a3b8;
            margin: 0;
        }
    }

    .pay-actions {
        margin-top: 30px;
    }

    .error-tip {
        margin-top: 30px;
        padding: 1rem;
        color: #ef4444;
        font-size: 0.9rem;
    }
}
</style>
